import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'
import type { LedgerRole } from './session.ts'

export interface Member {
  readonly subject: string
  readonly role: LedgerRole
  readonly name: string | undefined
}

export const memberKeys = {
  list: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'members'),
  invalidatedByWrite: (ledgerId: string): Invalidations => [ledgerKey(ledgerId, 'members')],
} as const

export interface MembersRepository {
  list(ledgerId: string, signal?: AbortSignal): Promise<readonly Member[]>
  create(ledgerId: string, input: Schemas['AddMemberRequest']): Promise<void>
  /** 204 No Content on the wire - unlike create, there is no body to map. */
  updateRole(ledgerId: string, subject: string, role: LedgerRole): Promise<void>
  remove(ledgerId: string, subject: string): Promise<void>
}

export function createMembersRepository(client: TalerClient): MembersRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/members`
  const one = (ledgerId: string, subject: string) => `${base(ledgerId)}/${encodeURIComponent(subject)}`

  return {
    async list(ledgerId, signal) {
      const response = await client.call<Schemas['LedgerMemberResponse'][]>({
        method: 'GET',
        path: base(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map(toMember)
    },

    async create(ledgerId, input) {
      await client.call<void, Schemas['AddMemberRequest']>({
        method: 'POST',
        path: base(ledgerId),
        body: input,
      })
    },

    async updateRole(ledgerId, subject, role) {
      await client.call<undefined, Schemas['ChangeMemberRoleRequest']>({
        method: 'PATCH',
        path: one(ledgerId, subject),
        body: { role },
      })
    },

    async remove(ledgerId, subject) {
      await client.call<undefined>({
        method: 'DELETE',
        path: one(ledgerId, subject),
      })
    },
  }
}

function toMember(response: Schemas['LedgerMemberResponse']): Member {
  return { subject: response.subject, role: response.role, name: response.name }
}
